import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { FormField, FormSection } from "@/components/forms/FormField";
import { AddressFields } from "@/components/forms/AddressFields";
import { EMPTY_ADDRESS, addressErrors, validIban } from "@/lib/validators";

const isAdult = (d) => {
  if (!d) return false;
  const b = new Date(d), t = new Date();
  const age = t.getFullYear() - b.getFullYear() - (t < new Date(t.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
  return age >= 18 && age <= 110;
};

export default function CompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState(user.profile?.address || EMPTY_ADDRESS);
  const [birth, setBirth] = useState(user.profile?.birth_date || "");
  const [iban, setIban] = useState(user.profile?.iban || "");
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = { ...addressErrors(address) };
    if (!isAdult(birth)) errs.birth = "Devi essere maggiorenne";
    if (!validIban(iban)) errs.iban = "IBAN non valido";
    setErrors(errs);
    setError("");
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const { data } = await api.put("/profile", { address, birth_date: birth, iban });
      setUser(data);
      toast.success("Profilo confermato");
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:py-14" data-testid="complete-profile-page">
      <div className="rise rounded-3xl border border-[#E2E5DC] bg-white p-6 shadow-[0_20px_60px_-30px_rgba(15,45,94,0.35)] sm:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0F2D5E]">Ultimo passo</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Conferma il tuo profilo</h1>
        <p className="mt-2 text-sm text-[#495850]">Ciao {user.first_name}, ci servono questi dati per pagarti i compensi delle segnalazioni concluse.</p>
        <form onSubmit={submit} noValidate className="mt-8 space-y-10">
          <FormSection title="Indirizzo di residenza"><AddressFields value={address} onChange={setAddress} errors={errors} prefix="profile" /></FormSection>
          <FormSection title="Dati personali e pagamenti">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField id="profile-birth" label="Data di nascita" error={errors.birth}>
                <Input id="profile-birth" type="date" className="h-12" value={birth} onChange={(e) => setBirth(e.target.value)} data-testid="profile-birth-input" />
              </FormField>
              <FormField id="profile-iban" label="IBAN per i pagamenti" error={errors.iban}>
                <Input id="profile-iban" className="h-12 font-mono uppercase" placeholder="IT60 X054 2811 1010 0000 0123 456" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} data-testid="profile-iban-input" />
              </FormField>
            </div>
            <p className="flex items-start gap-2 text-xs text-[#75867E]"><ShieldCheck className="h-4 w-4 shrink-0" /> I tuoi dati sono visibili solo a te e all'amministrazione di iosegnalo.net.</p>
          </FormSection>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="profile-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-12 w-full rounded-full bg-[#0F2D5E] text-base hover:bg-[#0B2250]" data-testid="profile-submit-btn">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Conferma profilo
          </Button>
        </form>
      </div>
    </main>
  );
}
